import { makeStyles } from '@material-ui/styles';
import { Radio } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  orderStage,
  BookingJourney,
  INSTANT_BOOKING_STEP,
  INSTANT_BOOKING_STATUS,
} from 'common/constants/booking';
import SelectFilter from 'common/components/select-filter';
import { orderAll, opportunityStages } from './useTableFilters';

const useStyles = makeStyles(
  theme => ({
    filter: {
      ...theme.flex.mainStart,
      marginBottom: '16px',

      '& > *:not(:first-child)': { marginLeft: 8 },
    },
    radioBtn: {
      ...theme.flex.mainCenter,
    },
    radioBtnCount: {
      height: 16,
      minWidth: 16,
      marginTop: 7,
      marginLeft: 4,
      textAlign: 'center',
      fontSize: '10px',
      lineHeight: '18px',
      color: theme.colors.gray1,
      borderRadius: theme.borderRadius.full,
      backgroundColor: theme.colors.red6,
    },
  }),
  { name: 'BookingStageAndPropertyNameFilters' },
);

const BookingStageAndPropertyNameFilters = ({
  bookingType,
  bookingStage,
  setBookingStage,
  pendingConfirmationOrderCount,
  pendingRefundOrderCount,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const orderStagesRadioGroup = useMemo(
    () => [
      { value: orderAll, label: t('landlord.long_tail.booking.booking_status.all') },
      {
        value: orderStage.WAITING_FOR_CONFIRMATION,
        label: t(
          `landlord.long_tail.booking.booking_status.${orderStage.WAITING_FOR_CONFIRMATION.toLocaleLowerCase()}`,
        ),
        count: pendingConfirmationOrderCount,
      },
      {
        value: orderStage.REFUNDING,
        label: t(
          `landlord.long_tail.booking.booking_status.${orderStage.REFUNDING.toLocaleLowerCase()}`,
        ),
        count: pendingRefundOrderCount,
      },
    ],
    [pendingConfirmationOrderCount, pendingRefundOrderCount, t],
  );

  const bookingStagesRadioGroup = useMemo(
    () => [
      { value: orderAll, label: t('landlord.long_tail.booking.booking_status.all') },
      {
        value: opportunityStages.COMPLETED,
        label: t(
          `landlord.long_tail.booking.booking_status.${opportunityStages.COMPLETED.toLocaleLowerCase()}`,
        ),
      },
      {
        value: opportunityStages.CANCELLATION,
        label: t(
          `landlord.long_tail.booking.booking_status.${opportunityStages.CANCELLATION.toLocaleLowerCase()}`,
        ),
      },
    ],
    [t],
  );

  const instantBookingsRadioGroup = useMemo(
    () => [
      { value: orderAll, label: t('landlord.long_tail.booking.booking_status.all') },
      {
        value: INSTANT_BOOKING_STEP.FILLING_PERSONAL_INFO,
        label: t(
          `landlord.long_tail.booking.list.instant_booking.${INSTANT_BOOKING_STEP.FILLING_PERSONAL_INFO.toLocaleLowerCase()}`,
        ),
      },
      {
        value: INSTANT_BOOKING_STEP.SIGNING_AGREEMENT,
        label: t(
          `landlord.long_tail.booking.list.instant_booking.${INSTANT_BOOKING_STEP.SIGNING_AGREEMENT.toLocaleLowerCase()}`,
        ),
      },
      {
        value: INSTANT_BOOKING_STEP.MAKING_PAYMENT,
        label: t(
          `landlord.long_tail.booking.list.instant_booking.${INSTANT_BOOKING_STEP.MAKING_PAYMENT.toLocaleLowerCase()}`,
        ),
      },
    ],
    [t],
  );

  const bookingFilterRadioGroup = useMemo(() => {
    if (bookingType === BookingJourney.SEMI_AUTOMATIC) {
      return orderStagesRadioGroup;
    } else if (bookingType === BookingJourney.INSTANT_BOOK) {
      return instantBookingsRadioGroup;
    } else {
      return bookingStagesRadioGroup;
    }
  }, [bookingType, orderStagesRadioGroup, instantBookingsRadioGroup, bookingStagesRadioGroup]);

  const handleClickRadioGroup = e => {
    setBookingStage(e?.target?.value);
  };

  const statusOptions = useMemo(() => {
    if (bookingType === BookingJourney.SEMI_AUTOMATIC) {
      const orderStages = Object.keys(orderStage).filter(
        item => ![orderStage.WAITING_FOR_CONFIRMATION, orderStage.REFUNDING].includes(item),
      );

      return orderStages.map(item => ({
        value: item,
        label: t(`landlord.long_tail.booking.booking_status.${item.toLocaleLowerCase()}`),
      }));
    } else {
      return Object.keys(INSTANT_BOOKING_STATUS).map(item => ({
        value: item,
        label: t(
          `landlord.long_tail.booking.list.instant_booking_status.${item.toLocaleLowerCase()}`,
        ),
      }));
    }
  }, [t, bookingType]);

  const btnText = useMemo(() => {
    if (bookingType === BookingJourney.SEMI_AUTOMATIC) {
      return [orderAll, orderStage.WAITING_FOR_CONFIRMATION, orderStage.REFUNDING].includes(
        bookingStage,
      )
        ? t('booking_status')
        : t(`landlord.long_tail.booking.booking_status.${bookingStage.toLocaleLowerCase()}`);
    } else {
      return [
        orderAll,
        INSTANT_BOOKING_STEP.FILLING_PERSONAL_INFO,
        INSTANT_BOOKING_STEP.MAKING_PAYMENT,
        INSTANT_BOOKING_STEP.SIGNING_AGREEMENT,
      ].includes(bookingStage)
        ? t('landlord.reconciliation_list.filter.other_status.label')
        : t(
            `landlord.long_tail.booking.list.instant_booking_status.${bookingStage.toLocaleLowerCase()}`,
          );
    }
  }, [bookingStage, bookingType, t]);

  return (
    <div className={classes.filter}>
      <Radio.Group value={bookingStage} onChange={handleClickRadioGroup}>
        <For of={bookingFilterRadioGroup} each="item">
          <Radio.Button value={item?.value} key={item?.value}>
            <div className={classes.radioBtn}>
              <span>{item.label}</span>
              <If condition={item?.count}>
                <div className={classes.radioBtnCount}>
                  {item?.count > 99 ? '99+' : item?.count}
                </div>
              </If>
            </div>
          </Radio.Button>
        </For>
      </Radio.Group>

      <If
        condition={
          bookingType === BookingJourney.SEMI_AUTOMATIC ||
          bookingType === BookingJourney.INSTANT_BOOK
        }
      >
        <SelectFilter
          value={bookingStage === orderAll ? undefined : bookingStage}
          options={statusOptions}
          btnText={btnText}
          filterIcon={<FilterOutlined />}
          onSelect={setBookingStage}
        />
      </If>
    </div>
  );
};

BookingStageAndPropertyNameFilters.propTypes = {
  bookingStage: PropTypes.string,
  bookingType: PropTypes.string,
  setBookingStage: PropTypes.func,
  pendingConfirmationOrderCount: PropTypes.number,
  pendingRefundOrderCount: PropTypes.number,
};

export default BookingStageAndPropertyNameFilters;
